import React, { type JSX } from 'react';

import {
    resetFilters,
    setScheduledEvents,
    toggleScheduleLoader,
} from '@fiji/common/src/features/schedule/schedulerSlice';

import { useDebounce } from 'hooks';
import { selectedOrg } from '@fiji/common/src/features/orgManagement/orgSlice';
import { getFromToFormat } from './utils/helpers';
import { useGetAllSchedulesQuery } from '@fiji/common/src/features/schedule/scheduleApi';
import { Schedule, useHandleSchedule } from 'components/Schedule';
import { DevicesGrid, DeviceTypesGrid } from './ViewSchedules';
import { useAppDispatch, useTypedSelector } from '@fiji/common';
import { CustomTransComponent } from 'components';
import { ActionFilterDropDown, DeviceTypeFilterDropDown } from './common';
import { useTransformData } from 'hooks/useTransformData';
import { ScheduleDetails } from './ScheduleDetails';

export const ScheduleCalendar = React.memo(({ searchKey }: any): JSX.Element => {
    const dispatch = useAppDispatch();
    const detailsModalRef = React.useRef<any>(null);

    const { viewMode, groupingEnabled, selectedDate } = useHandleSchedule((data: any): any => handleEventClick(data));
    const currentOrg = useTypedSelector(selectedOrg);
    const [, debouncedValue] = useDebounce(undefined, undefined, searchKey);

    const selectedNode = useTypedSelector((state) => state['common']['selectedNode']);

    const filters = useTypedSelector((state) => state['scheduler']?.['filters']);
    const selectedDeviceType = useTypedSelector((state) => state['scheduler']['selectedDeviceType']);
    const dailyScheduledEvents = useTypedSelector((state) => state['scheduler']['dailyScheduledEvents']);
    const { timezone: selectedTimezone } = useTransformData();

    function handleEventClick(event: any): any {
        detailsModalRef?.current?.handleScheduleDetails(event);
    }

    const {
        isError,
        isSuccess,
        currentData: allSchedules,
    } = useGetAllSchedulesQuery(
        {
            page: 0,
            size: 1000,
            filters: {
                ...(!selectedNode?.wrapperDeviceType
                    ? { groupId: [selectedNode?.id ?? currentOrg?.id] }
                    : selectedNode?.wrapperDeviceType === 'Gateway'
                    ? { gatewayId: [selectedNode?.id] }
                    : { deviceId: [selectedNode?.id] }),
                ...getFromToFormat(viewMode, selectedDate, selectedTimezone),
                ...(viewMode === 'week' && Object.keys(filters ?? {})?.length && filters),
            },
            ...(debouncedValue && { searchKey: debouncedValue }),
        },
        { skip: !viewMode || !selectedTimezone, refetchOnMountOrArgChange: true }
    );

    React.useEffect(() => {
        if (allSchedules?.data && viewMode === 'day') {
            dispatch(setScheduledEvents({ isGrouped: groupingEnabled, value: allSchedules?.data?.records }));
        }
        return () => {
            dispatch(resetFilters());
        };
    }, [allSchedules, groupingEnabled, viewMode]);

    React.useEffect(() => {
        if (isSuccess || isError) {
            dispatch(toggleScheduleLoader(false));
        }
    }, [isSuccess, isError]);
    return (
        <>
            <Schedule
                {...(viewMode === 'day' && {
                    groupingEnabled: true,
                })}
                groupingLabel={<CustomTransComponent translationKey={'COMMON:GROUP_BY_DEVICE_TYPE'} />}
                gridLabel={<CustomTransComponent translationKey={'COMMON:DEVICE'} />}
                rightComponents={[
                    { id: '2dd2x2', component: ActionFilterDropDown },
                    { id: 'daf3qfqgq', component: DeviceTypeFilterDropDown },
                ]}
                selectedDeviceType={selectedDeviceType?.id}
                schedules={viewMode === 'day' ? dailyScheduledEvents : allSchedules?.data?.records}
                leftGrid={groupingEnabled ? <DeviceTypesGrid /> : <DevicesGrid />}
            />
            <ScheduleDetails key="dqf3fqg" ref={detailsModalRef} />
        </>
    );
});
